import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CalendarBlank, Check, CheckCircle, Clock, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { createBooking, fetchRooms } from "../lib/api";
import { format } from "date-fns";
import { it as itLocale, enUS } from "date-fns/locale";

export default function BookingModal({ open, onOpenChange, initialRoom }) {
  const { t, lang } = useLang();
  const locale = lang === "it" ? itLocale : enUS;
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    guests: 2,
    room_slug: initialRoom || "",
    message: "",
  });
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load rooms once (so the selector has real names + prices and we can compute the price estimate).
  useEffect(() => {
    if (!open) return;
    if (rooms.length) return;
    fetchRooms().then(setRooms).catch(() => setRooms([]));
  }, [open, rooms.length]);

  // Compute nights and price estimate.
  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    const ms = checkOut.getTime() - checkIn.getTime();
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  }, [checkIn, checkOut]);

  const selectedRoom = useMemo(
    () => rooms.find((r) => r.slug_it === form.room_slug || r.slug_en === form.room_slug) || null,
    [rooms, form.room_slug]
  );

  const priceFrom = useMemo(() => {
    if (selectedRoom) return selectedRoom.price_from;
    if (!rooms.length) return null;
    return Math.min(...rooms.map((r) => r.price_from || 0).filter(Boolean));
  }, [selectedRoom, rooms]);

  const total = nights && priceFrom ? nights * priceFrom : null;
  const datesSelected = Boolean(checkIn && checkOut && nights > 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!checkIn || !checkOut) {
      toast.error(t.booking.pickDate);
      return;
    }
    setLoading(true);
    try {
      await createBooking({
        ...form,
        check_in: format(checkIn, "yyyy-MM-dd"),
        check_out: format(checkOut, "yyyy-MM-dd"),
        lang,
      });
      toast.success(t.booking.success);
      onOpenChange(false);
      setForm({ name: "", email: "", phone: "", guests: 2, room_slug: "", message: "" });
      setCheckIn(null);
      setCheckOut(null);
    } catch (err) {
      toast.error(t.booking.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[600px] bg-[#0c0c0e] border-white/10 text-white p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
        data-testid="booking-modal"
      >
        <div className="p-8 lg:p-10">
          <DialogHeader>
            <div className="overline mb-2">{t.hero.overline}</div>
            <DialogTitle className="font-serif text-3xl lg:text-4xl leading-tight text-white">
              {t.booking.title}
            </DialogTitle>
            <DialogDescription className="text-white/60 mt-2 text-sm">
              {t.booking.subtitle}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="booking-form">
            <div className="grid grid-cols-2 gap-4">
              <DatePick label={t.booking.checkIn} value={checkIn} onChange={setCheckIn} locale={locale} testid="checkin" placeholder={t.booking.pickDate} />
              <DatePick label={t.booking.checkOut} value={checkOut} onChange={setCheckOut} locale={locale} testid="checkout" placeholder={t.booking.pickDate} minDate={checkIn} />
            </div>

            <Field label={t.booking.room}>
              <Select value={form.room_slug || "__any"} onValueChange={(v) => set("room_slug", v === "__any" ? "" : v)}>
                <SelectTrigger data-testid="select-room" className="booking-input h-[46px]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0c0c0e] border-white/10 text-white">
                  <SelectItem value="__any">{t.booking.roomAny}</SelectItem>
                  {rooms.map((r) => (
                    <SelectItem key={r.id} value={lang === "it" ? r.slug_it : r.slug_en}>
                      {lang === "it" ? r.name_it : r.name_en} — {t.rooms.from} €{r.price_from}/{t.rooms.perNight}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {/* Price + availability summary */}
            {datesSelected && priceFrom && (
              <div
                className="flex items-center justify-between gap-4 border border-[#C5A059]/30 bg-[#C5A059]/5 px-5 py-4"
                data-testid="booking-summary"
              >
                <div className="flex items-center gap-3 text-sm">
                  <CheckCircle size={20} weight="fill" className="text-emerald-400 shrink-0" />
                  <span className="text-emerald-300 font-medium">{t.booking.available}</span>
                </div>
                <div className="text-right">
                  <div className="text-xs text-white/50 uppercase tracking-[0.15em]">
                    {selectedRoom ? t.booking.estimated : t.booking.totalFrom}
                  </div>
                  <div className="font-serif text-2xl text-[#C5A059] leading-tight">
                    €{total}
                  </div>
                  <div className="text-xs text-white/40">
                    {t.booking.nights(nights)} × €{priceFrom}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <Field label={t.booking.name}>
                <input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="input-name" className="booking-input" />
              </Field>
              <Field label={t.booking.email}>
                <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="input-email" className="booking-input" />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label={t.booking.phone}>
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="input-phone" className="booking-input" />
              </Field>
              <Field label={t.booking.guests}>
                <Select value={String(form.guests)} onValueChange={(v) => set("guests", Number(v))}>
                  <SelectTrigger data-testid="select-guests" className="booking-input h-[46px]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0c0c0e] border-white/10 text-white">
                    {[1, 2, 3, 4].map((n) => (<SelectItem key={n} value={String(n)}>{n}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label={t.booking.message}>
              <textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} data-testid="input-message" className="booking-input resize-none" />
            </Field>

            <button
              type="submit"
              disabled={loading}
              data-testid="booking-submit"
              className="w-full btn-gold py-4 text-xs uppercase tracking-[0.2em] font-medium mt-2 inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "..." : <>{t.booking.submit} <Check size={16} weight="bold" /></>}
            </button>

            {/* Trust footer */}
            <div className="pt-5 mt-2 border-t border-white/10 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-[0.15em] text-white/50">
              <div className="flex items-center gap-1.5 justify-center">
                <ShieldCheck size={14} weight="light" className="text-[#C5A059]" />
                <span className="leading-tight text-center">{t.booking.trust.direct}</span>
              </div>
              <div className="flex items-center gap-1.5 justify-center">
                <Check size={14} weight="light" className="text-[#C5A059]" />
                <span className="leading-tight text-center">{t.booking.trust.bestRate}</span>
              </div>
              <div className="flex items-center gap-1.5 justify-center">
                <Clock size={14} weight="light" className="text-[#C5A059]" />
                <span className="leading-tight text-center">{t.booking.response}</span>
              </div>
            </div>
          </form>
        </div>
        <style>{`
          .booking-input {
            width: 100%;
            background: #18181b;
            border: 1px solid rgba(255,255,255,0.08);
            color: #fff;
            padding: 12px 14px;
            font-size: 14px;
            outline: none;
            transition: border 200ms ease;
          }
          /* Radix SelectValue renders the selected item text in a nested
             <span> that doesn't inherit color in some dark-mode shadcn
             configurations -> the selected room becomes invisible.
             Force white on every descendant of a booking-input. */
          .booking-input, .booking-input * { color: #fff; }
          .booking-input:focus { border-color: #C5A059; }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline block mb-2 text-white/60">{label}</span>
      {children}
    </label>
  );
}

function DatePick({ label, value, onChange, locale, testid, placeholder, minDate }) {
  return (
    <div>
      <span className="overline block mb-2 text-white/60">{label}</span>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`date-${testid}`}
            className="w-full bg-[#18181b] border border-white/10 px-4 py-3 text-left text-sm flex items-center justify-between hover:border-[#C5A059]/50 transition"
          >
            <span className={value ? "text-white" : "text-white/40"}>
              {value ? format(value, "dd MMM yyyy", { locale }) : placeholder}
            </span>
            <CalendarBlank size={16} className="text-[#C5A059]" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-[#0c0c0e] border-white/10">
          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            locale={locale}
            disabled={minDate ? { before: minDate } : { before: new Date() }}
            className="bg-[#0c0c0e] text-white"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
